import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class DemoVendorService {
  public static readonly DEMO_EMAIL = 'demovendor@gm.com';

  private demoUids: Set<string> = new Set();
  private isLoaded = false;
  private loadPromise: Promise<Set<string>> | null = null;

  constructor(private firestore: Firestore) {
    this.initDemoUids();
  }

  /**
   * Fetches and caches all Firestore UIDs associated with the demo vendor account
   * (including parent vendor account and any worker accounts linked to it).
   */
  async initDemoUids(): Promise<Set<string>> {
    if (this.isLoaded) return this.demoUids;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      try {
        const usersRef = collection(this.firestore, 'users-list');
        const q1 = query(usersRef, where('email', '==', DemoVendorService.DEMO_EMAIL));
        const q2 = query(usersRef, where('accountdetails.email', '==', DemoVendorService.DEMO_EMAIL));

        const [snap1, snap2] = await Promise.all([
          getDocs(q1).catch(() => null),
          getDocs(q2).catch(() => null),
        ]);

        if (snap1) {
          snap1.docs.forEach((doc) => this.demoUids.add(doc.id));
        }
        if (snap2) {
          snap2.docs.forEach((doc) => this.demoUids.add(doc.id));
        }

        // Also resolve any worker sub-accounts whose parent is a demo vendor
        if (this.demoUids.size > 0) {
          for (const pUid of Array.from(this.demoUids)) {
            const qw = query(usersRef, where('parentUid', '==', pUid));
            const snapW = await getDocs(qw).catch(() => null);
            if (snapW) {
              snapW.docs.forEach((doc) => this.demoUids.add(doc.id));
            }
          }
        }
      } catch (err) {
        console.error('Error loading demo vendor UIDs:', err);
      } finally {
        this.isLoaded = true;
      }
      return this.demoUids;
    })();

    return this.loadPromise;
  }

  /**
   * Synchronously checks if an equipment object belongs to the demo vendor.
   */
  isDemoEquipment(eq: any): boolean {
    if (!eq) return false;
    if (eq.isDemo === true || eq.isDemo === 'true') return true;

    const email = (
      eq.vendorEmail ||
      eq.bywhomEmail ||
      eq.email ||
      eq.userEmail ||
      ''
    ).toLowerCase().trim();
    if (email === DemoVendorService.DEMO_EMAIL) return true;

    const bywhom = eq.bywhom || eq.userId || eq.vendorId || eq.createdBy;
    if (bywhom && this.demoUids.has(bywhom)) return true;

    return false;
  }

  /**
   * Checks if an equipment item is demo, ensuring demo UIDs are loaded first.
   */
  async isDemoEquipmentAsync(eq: any): Promise<boolean> {
    if (!this.isLoaded) {
      await this.initDemoUids();
    }
    return this.isDemoEquipment(eq);
  }

  /**
   * Attaches an `isDemo: boolean` property to a list of equipments.
   */
  async attachDemoFlags<T extends Record<string, any>>(list: T[]): Promise<(T & { isDemo?: boolean })[]> {
    await this.initDemoUids();
    return list.map((item) => ({
      ...item,
      isDemo: this.isDemoEquipment(item),
    }));
  }
}
